import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {VetClinicService} from './vet-clinic.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Guard zarządzający dostępem do modułu Zamówień (Orders).
 * Weryfikuje po stronie serwera API, czy aktualnie zalogowany użytkownik posiada
 * odpowiednie uprawnienia do przeglądania historii transakcji i zamówień.
 */
@Injectable({
  providedIn: 'root'
})
export class OrdersAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private vetClinicService: VetClinicService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) kodu modułu zamówień.
   * Zabezpiecza moduł finansowy przed załadowaniem do przeglądarki osoby nieuprawnionej,
   * wymuszając autoryzację na dedykowanym endpointcie backendowym przed pobraniem plików JS.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku pomyślnej autoryzacji).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/orders-access', {}).pipe(take(1),
      catchError(err => {
        if (err.status === 401) {
          this.router.navigate(['signin']).then(() => {
            this.authService.logout();
          });
        }
        return throwError(err);
      }),
      map(response => {
        console.log('check - orders - ok');
        return true;
      }));
  }

  /**
   * Weryfikuje uprawnienia przed fizycznym wyrenderowaniem widoku listy zamówień.
   * Obsługuje błędy wygaśnięcia sesji (HTTP 401), a w przypadku braku wystarczających
   * uprawnień aplikacyjnych, wykorzystuje klasę CommonUtils do zaprezentowania komunikatu błędu.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/orders-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - orders - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
